import { useEffect, useState } from "react"
import { Wrench, RefreshCw, Home, CheckCircle } from "lucide-react"
import useMaintenanceMode from "../hooks/useMaintenanceMode"

export default function MaintenancePage() {
  const { 
    isInMaintenance, 
    isChecking, 
    checkCount,
    checkMaintenanceStatus 
  } = useMaintenanceMode(600000)

  // Animation de rotation pour l'icône de maintenance
  const [isSpinning, setIsSpinning] = useState(false)

  const handleRetry = async () => {
    setIsSpinning(true)
    
    try {
      const stillInMaintenance = await checkMaintenanceStatus()
      
      if (!stillInMaintenance) {
        // La maintenance est terminée, rediriger vers l'accueil
        setTimeout(() => {
          window.location.href = '/'
        }, 1000)
      }
    } catch (error) {
      console.error('Erreur lors de la vérification:', error)
    } finally {
      setTimeout(() => {
        setIsSpinning(false)
      }, 2000)
    }
  }

  // Redirection automatique si la maintenance est terminée
  useEffect(() => {
    if (!isInMaintenance && checkCount > 0) {
      // Afficher un message de succès puis rediriger
      setTimeout(() => {
        window.location.href = '/'
      }, 3000)
    }
  }, [isInMaintenance, checkCount])

  const goHome = () => {
    window.location.href = '/'
  }

  return (
    <div className="h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-6">
      <div className="w-full max-w-lg text-center">
        
        {!isInMaintenance && checkCount > 0 ? (
          // État: Maintenance terminée
          <>
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-6 shadow-lg">
              <CheckCircle size={40} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-green-600 mb-4">
              Maintenance terminée !
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Redirection automatique vers l'accueil...
            </p>
          </>
        ) : (
          // État: Maintenance en cours
          <>
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-6 shadow-lg">
              <Wrench 
                size={40} 
                className={`text-white transition-transform duration-500 ${isSpinning ? 'animate-spin' : ''}`}
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              🚧 Maintenance en cours
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Nous améliorons notre plateforme.<br />
              Nous serons de retour très bientôt !
            </p>
          </>
        )}

        {/* Boutons d'action */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <button
            onClick={handleRetry}
            disabled={isSpinning || isChecking}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:hover:scale-100 transition-all duration-200 disabled:cursor-not-allowed"
          >
            <RefreshCw size={18} className={isSpinning || isChecking ? 'animate-spin' : ''} />
            {isSpinning || isChecking ? 'Vérification...' : 'Réessayer'}
          </button>

          <button
            onClick={goHome}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-200 hover:border-gray-300 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            <Home size={18} />
            Accueil
          </button>
        </div>

        {/* Contact simplifié */}
        <div className="text-sm text-gray-500">
          <p className="mb-2">Besoin d'aide ?</p>
          <a 
            href="mailto:support@ay-ecampus.com" 
            className="text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
          >
            support@ay-ecampus.com
          </a>
        </div>
      </div>
    </div>
  )
}